import React, { Fragment, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import axios from "axios";
import { Snackbar } from "@material-ui/core";
import {
  Card,
  CardBody,
  CardHeader,
  CardHeaderToolbar,
} from "../../../../_metronic/_partials/controls";
import { useSubheader } from "../../../../_metronic/layout";
import { SnackbarWrapper } from "../../Snackbar/SnackbarWrapper";
import { BranchCourseCustomizationForm } from "./BranchCourseCustomizationForm";

const CREATE_URL = "api/branch-course-customization/new";
const EDIT_URL = "api/branch-course-customization/edit/";
const GET_URL = "api/branch-course-customization/";
const BRANCH_COURSES_URL = "/api/branch-course/";
const PROGRAM_TYPE_URL = "api/batches/program-type";

const emptyValues = {
  name: "",
  branch_course_id: [],
  message_type: "",
  message_details: "",
  active_id: 1,
};

export function BranchCourseCustomizationEdit({ history, match }) {
  const id = match?.params?.id;
  const isEdit = !!id;

  const subheader = useSubheader();
  subheader.setTitle(
    isEdit ? "Edit Customize Stream Message" : "New Customize Stream Message"
  );

  const btnRef = useRef();
  const [branchCourses, setBranchCourses] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [initValues, setInitValues] = useState(emptyValues);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Something went wrong");

  const branch = useSelector(
    (state) => state.auth.branches.currentBranch,
    shallowEqual
  );

  const authToken = useSelector(
    (state) => state.auth.authToken,
    shallowEqual
  );

  useEffect(() => {
    axios
      .get(PROGRAM_TYPE_URL)
      .then((res) => setProgramTypes(res.data.results))
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    if (branch?.branch_id) {
      axios
        .get(BRANCH_COURSES_URL + branch.branch_id, {
          headers: { "x-auth-token": authToken },
        })
        .then((res) => setBranchCourses(res.data))
        .catch((err) => console.log(err));
    }
  }, [branch?.branch_id]);

  useEffect(() => {
    if (isEdit) {
      axios
        .get(GET_URL + id, { headers: { "x-auth-token": authToken } })
        .then((res) => {
          const data = res.data;
          setInitValues({
            name: data.name,
            branch_course_id: (data.branch_course_ids || []).map((bcId) => ({
              value: bcId.toString(),
            })),
            message_type: data.message_type,
            message_details: data.message_details,
            active_id: data.active_id,
          });
        })
        .catch((err) => console.log(err));
    }
  }, [id]);

  const handleSubmit = async (values) => {
    try {
      const { branch_course_id, ...rest } = values;
      const payload = {
        ...rest,
        branch_course_ids: branch_course_id,
      };

      if (isEdit) {
        await axios.put(EDIT_URL + id, payload, {
          headers: {
            "x-auth-token": authToken,
            "Content-Type": "application/json",
          },
        });
      } else {
        await axios.post(CREATE_URL, payload, {
          headers: {
            "x-auth-token": authToken,
            "Content-Type": "application/json",
          },
        });
      }

      setSuccessOpen(true);
      setTimeout(() => {
        history.push("/branch-course-customization/list");
      }, 1000);
    } catch (error) {
      console.log(error);
      if (error.response?.status === 409) {
        const base = error.response.data?.message || "Duplicate entry detected";
        const idMatch = base.match(/branch_course_id\(s\):\s*([\d,\s]+)$/);
        if (idMatch) {
          const conflictIds = idMatch[1]
            .split(",")
            .map((s) => parseInt(s.trim(), 10));
          const conflictNames = conflictIds
            .map((id) => {
              const course = branchCourses.find((c) => c.branch_course_id === id);
              return course ? `${course.course_code} - ${course.course_name}` : null;
            })
            .filter(Boolean)
            .join(", ");
          setErrorMessage(
            conflictNames
              ? base.replace(idMatch[0], `course(s): ${conflictNames}`)
              : base
          );
        } else {
          setErrorMessage(base);
        }
      } else {
        setErrorMessage("Something went wrong");
      }
      setErrorOpen(true);
    }
  };

  const handleSaveClick = () => {
    if (btnRef && btnRef.current) {
      btnRef.current.click();
    }
  };

  const handleClose = () => {
    setSuccessOpen(false);
    setErrorOpen(false);
    setErrorMessage("Something went wrong");
  };

  return (
    <Fragment>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={successOpen || errorOpen}
        autoHideDuration={successOpen ? 3000 : 6000}
        onClose={handleClose}
      >
        <SnackbarWrapper
          onClose={handleClose}
          variant={successOpen ? "success" : "error"}
          message={
            successOpen
              ? `Customize Stream Message ${isEdit ? "updated" : "saved"} successfully`
              : errorMessage
          }
        />
      </Snackbar>

      <Card>
        <CardHeader
          title={
            isEdit
              ? "Edit Customize Stream Message"
              : "New Customize Stream Message"
          }
        >
          <CardHeaderToolbar>
            <button
              type="button"
              onClick={() => history.push("/branch-course-customization/list")}
              className="btn btn-light"
            >
              <i className="fa fa-arrow-left"></i>
              Back
            </button>
            {`  `}
            <button
              type="submit"
              className="btn btn-primary ml-2"
              onClick={handleSaveClick}
            >
              Save
            </button>
          </CardHeaderToolbar>
        </CardHeader>
        <CardBody>
          <ul className="nav nav-tabs nav-tabs-line" role="tablist">
            <li className="nav-item">
              <a className="nav-link active" data-toggle="tab" role="tab">
                Customize Stream Message Info
              </a>
            </li>
          </ul>
          <div className="mt-5">
            <BranchCourseCustomizationForm
              values={initValues}
              btnRef={btnRef}
              onSubmit={handleSubmit}
              branchCourses={branchCourses}
              programTypes={programTypes}
              isEdit={isEdit}
            />
          </div>
        </CardBody>
      </Card>
    </Fragment>
  );
}
